import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { inr } from "@/lib/format";
import { Skeleton, SkuThumb } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RefreshCw, Search, Download, ExternalLink, Wrench, AlertTriangle, ArrowUp, ArrowDown } from "lucide-react";

const SortTh = ({ label, k, sort, setSort, align = "right" }) => {
  const active = sort.key === k;
  return (
    <th
      className={`text-${align} px-3 py-2.5 font-semibold cursor-pointer select-none hover:text-foreground transition-colors`}
      onClick={() => setSort({ key: k, dir: active && sort.dir === "desc" ? "asc" : "desc" })}
      data-testid={`li-sort-${k}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active && (sort.dir === "desc" ? <ArrowDown className="h-3 w-3" aria-hidden="true" /> : <ArrowUp className="h-3 w-3" aria-hidden="true" />)}
      </span>
    </th>
  );
};

const Kpi = ({ label, value, sub, testId, tone }) => (
  <div className="bg-card border border-border rounded-lg p-5 shadow-sm" data-testid={testId}>
    <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">{label}</p>
    <p className={`font-heading text-2xl sm:text-3xl font-bold tracking-tight mt-2 ${tone || ""}`}>{value}</p>
    {sub && <p className="text-xs text-muted-foreground mt-1.5">{sub}</p>}
  </div>
);

export const ListingIssues = () => {
  const [data, setData] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [q, setQ] = useState("");
  const [reason, setReason] = useState("");
  const [sort, setSort] = useState({ key: "revenue_30d", dir: "desc" });

  const load = (refresh = false) =>
    api.get(`/tools/listing-issues${refresh ? "?refresh=true" : ""}`).then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);

  const sync = async () => {
    setSyncing(true);
    try {
      await api.post("/tools/listing-issues/sync");
      toast.info("Pulling suppressed listings from Amazon — usually 2–5 minutes");
      setTimeout(async () => { await load(true); setSyncing(false); toast.success("Listing issues refreshed"); }, 150000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refresh failed");
      setSyncing(false);
    }
  };

  const exportXlsx = async () => {
    try {
      const res = await api.get("/tools/listing-issues/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `suppressed-listings-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const rows = useMemo(() => {
    if (!data?.rows) return [];
    const query = q.trim().toLowerCase();
    let list = data.rows.filter((r) =>
      (!reason || r.reason === reason) &&
      (!query || (r.title || "").toLowerCase().includes(query) ||
        (r.sku || "").toLowerCase().includes(query) ||
        (r.asin || "").toLowerCase().includes(query) ||
        (r.fix || "").toLowerCase().includes(query)));
    list = [...list].sort((a, b) => {
      const va = a[sort.key] ?? -1, vb = b[sort.key] ?? -1;
      if (typeof va === "string") return sort.dir === "desc" ? String(vb).localeCompare(String(va)) : String(va).localeCompare(String(vb));
      return sort.dir === "desc" ? vb - va : va - vb;
    });
    return list;
  }, [data, q, reason, sort]);

  if (!data) return <div className="space-y-4"><Skeleton className="h-24" /><Skeleton className="h-64" /></div>;
  const k = data.kpis || {};

  return (
    <div className="space-y-6 min-w-0 max-w-full" data-testid="listing-issues">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold">Listing Issues</h2>
          <p className="text-xs text-muted-foreground">
            Suppressed / defective listings straight from Amazon&apos;s Fix Your Products report, with the exact fix Amazon wants
            {data.as_of && ` · data as of ${data.as_of}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="rounded-full" onClick={exportXlsx} data-testid="li-export-btn">
            <Download className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" /> Export Excel
          </Button>
          <Button size="sm" className="rounded-full" onClick={sync} disabled={syncing} data-testid="li-sync-btn">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${syncing ? "animate-spin" : ""}`} aria-hidden="true" />
            {syncing ? "Refreshing…" : "Refresh from Amazon"}
          </Button>
        </div>
      </div>

      {data.refreshing && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4 flex items-start gap-3" data-testid="li-refreshing">
          <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" aria-hidden="true" />
          <div>
            <p className="text-sm font-semibold">Fetching the report from Amazon</p>
            <p className="text-xs text-muted-foreground mt-1">{data.error || "Amazon takes 2–5 minutes to build this report. Reload this tab shortly."}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Suppressed listings" value={(k.suppressed || 0).toLocaleString("en-IN")} sub="not visible in search" testId="li-kpi-count" tone="text-red-600 dark:text-red-400" />
        <Kpi label="With stock sitting" value={(k.with_stock || 0).toLocaleString("en-IN")} sub={`${(k.units_stuck || 0).toLocaleString("en-IN")} units blocked`} testId="li-kpi-stock" />
        <Kpi label="Revenue at risk (30d)" value={inr(k.revenue_30d_at_risk || 0)} sub="these SKUs still sold this much" testId="li-kpi-revenue" tone="text-amber-600 dark:text-amber-400" />
        <Kpi label="Issue types" value={(data.reasons?.length || 0).toString()} sub="grouped by Amazon reason" testId="li-kpi-reasons" />
      </div>

      {data.reasons?.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-4 shadow-sm" data-testid="li-reasons">
          <h3 className="text-sm font-semibold mb-3">Why Amazon suppressed them</h3>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setReason("")}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${!reason ? "bg-blue-600 text-white border-blue-600" : "border-border text-muted-foreground hover:text-foreground"}`}
              data-testid="li-reason-all"
            >
              All ({k.suppressed || 0})
            </button>
            {data.reasons.map((r) => (
              <button
                key={r.reason}
                onClick={() => setReason(reason === r.reason ? "" : r.reason)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${reason === r.reason ? "bg-blue-600 text-white border-blue-600" : "border-border text-muted-foreground hover:text-foreground"}`}
                data-testid={`li-reason-${r.reason}`}
              >
                {r.reason || "Unspecified"} ({r.count})
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative w-full max-w-sm">
          <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search product, SKU, ASIN or issue…"
                 className="pl-9 h-9 rounded-full" data-testid="li-search-input" />
        </div>
        <span className="text-xs text-muted-foreground" data-testid="li-count">
          {rows.length.toLocaleString("en-IN")} listing{rows.length === 1 ? "" : "s"}{reason && ` · ${reason}`}
        </span>
      </div>

      <div className="bg-card border border-border rounded-lg shadow-sm overflow-x-auto w-full max-w-full min-w-0">
        <table className="w-full text-sm min-w-[1100px]" data-testid="li-table">
          <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-2.5"></th>
              <th className="text-left px-3 py-2.5 font-semibold">Product</th>
              <th className="text-left px-3 py-2.5 font-semibold">Status / reason</th>
              <th className="text-left px-3 py-2.5 font-semibold">What Amazon wants fixed</th>
              <SortTh label="Stock" k="stock" sort={sort} setSort={setSort} />
              <SortTh label="Units 30d" k="units_30d" sort={sort} setSort={setSort} />
              <SortTh label="Revenue 30d" k="revenue_30d" sort={sort} setSort={setSort} />
              <th className="text-right px-3 py-2.5 font-semibold">Fix</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 300).map((r, i) => (
              <tr key={`${r.sku}::${r.asin}::${i}`} className="border-t border-border hover:bg-muted/40 transition-colors" data-testid={`li-row-${r.sku}`}>
                <td className="py-2 pl-3 w-14"><SkuThumb sku={r.sku} alt={r.title} /></td>
                <td className="px-3 py-2">
                  <p className="font-medium max-w-[20rem] truncate" title={r.title}>{r.title || r.sku}</p>
                  <p className="font-mono text-[11px] text-muted-foreground">
                    {r.sku}
                    {r.listing_url && (
                      <a href={r.listing_url} target="_blank" rel="noreferrer" className="ml-1.5 text-blue-600 hover:underline" data-testid={`li-listing-${r.sku}`}>
                        {r.asin} <ExternalLink className="h-2.5 w-2.5 inline" aria-hidden="true" />
                      </a>
                    )}
                  </p>
                </td>
                <td className="px-3 py-2">
                  <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-500/10 text-red-600 dark:text-red-400">
                    {r.amazon_status}
                  </span>
                  <p className="text-xs text-muted-foreground mt-1">{r.reason || "—"}</p>
                  {r.since && <p className="text-[10px] text-muted-foreground">since {r.since}</p>}
                </td>
                <td className="px-3 py-2 max-w-[24rem]">
                  <p className="text-xs leading-relaxed">{r.fix || "No instruction provided by Amazon"}</p>
                </td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold">{(r.stock || 0).toLocaleString("en-IN")}</td>
                <td className="px-3 py-2 text-right tabular-nums">{(r.units_30d || 0).toLocaleString("en-IN")}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold">{inr(r.revenue_30d || 0)}</td>
                <td className="px-3 py-2 text-right">
                  <a href={r.fix_url} target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border border-border hover:bg-muted transition-colors"
                     data-testid={`li-fix-${r.sku}`}>
                    <Wrench className="h-3 w-3" aria-hidden="true" /> Seller Central
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && !data.refreshing && (
          <p className="text-sm text-muted-foreground p-6 text-center" data-testid="li-empty">
            {q || reason ? "No listings match your filters." : "No suppressed listings — every listing is live."}
          </p>
        )}
        {rows.length > 300 && (
          <p className="text-[11px] text-muted-foreground text-center py-2">Showing top 300 of {rows.length} — export to Excel for the full list</p>
        )}
      </div>
    </div>
  );
};
